import HCaptcha from '@hcaptcha/react-hcaptcha';
import { yupResolver } from '@hookform/resolvers/yup';
import Backdrop from 'components/backdrop';
import { Box, Button, Checkbox, Input, Select, Stack } from 'components/ui';
import { FC, FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { breakpoints } from 'styles/theme';
import { contactFormSchema } from 'utils/form-schema';
import { event } from 'utils/gtag';

const Wrapper = styled(Backdrop)`
  max-width: 1000px;
  margin: 0 auto;
`;

const FormGroup = styled.div`
  display: grid;
  gap: var(--space-3);

  @media screen and (min-width: ${breakpoints.medium}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SuccessMessage = styled(Box)`
  --background-color: var(--color-green);
  --padding: var(--space-2);

  display: flex;
  align-items: center;
  margin-bottom: var(--space-3);
`;

const ErrorMessage = styled(Box)`
  --background-color: var(--color-yellow);
  --padding: var(--space-2);

  display: flex;
  align-items: center;
  margin-bottom: var(--space-3);
`;

const DismissButton = styled(Button)`
  margin-left: auto;
`;

type FormData = {
  enquiryType: string;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  message: string;
  location?: string;
};

interface ContactFormProps {
  locations: Sanity.Schema.Location[];
}

const ContactForm: FC<ContactFormProps> = ({ locations }) => {
  const { register, handleSubmit, watch, formState, reset } = useForm<FormData>(
    {
      resolver: yupResolver(contactFormSchema),
    }
  );

  const defaultError = 'Error submitting form, try again later.';

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const [token, setToken] = useState<string | null>(null);
  const captchaRef = useRef<HCaptcha>(null);

  const clearSuccessMessage = () => setSuccessMessage('');
  const clearErrorMessage = () => setErrorMessage('');

  // Watch the enquiry type to handle conditional fields
  const enquiryType = watch('enquiryType', 'general');
  const buttonText =
    enquiryType === 'general' ? 'Get in touch' : 'Confirm booking';
  const messageLabel = enquiryType === 'general' ? 'Enquiry' : 'Availability';

  // Track formState
  const { errors, isSubmitting } = formState;

  const onExpire = () => {
    setErrorMessage(defaultError);
  };

  const onError = () => {
    setErrorMessage(defaultError);
  };

  const checkCaptcha = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (captchaRef.current) {
      captchaRef.current.execute();
    }
  };

  // Handle submit
  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        const res = await fetch('/api/contact', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        });
        const json = await res.json();
        setSuccessMessage(json.message);
        event({
          action: 'conversion',
          body: {
            send_to: 'AW-11266501072/0FerCOe8ncIYENDTpPwp',
            value: 2.0,
            currency: 'AUD',
          },
        });
        reset();
      } catch (error) {
        console.error(error);
        setErrorMessage(defaultError);
      } finally {
        captchaRef.current?.resetCaptcha();
        // Set token to null so that onVerify triggers useEffect properly on the next submission.
        setToken(null);
      }
    },
    [reset]
  );

  useEffect(() => {
    if (token) {
      // Token is set, can submit here
      handleSubmit(onSubmit)();
    }
  }, [handleSubmit, onSubmit, token]);

  return (
    <Wrapper>
      {successMessage && (
        <SuccessMessage>
          {successMessage}
          <DismissButton type='button' onClick={clearSuccessMessage}>
            Dismiss
          </DismissButton>
        </SuccessMessage>
      )}
      {errorMessage && (
        <ErrorMessage>
          {errorMessage}
          <DismissButton type='button' onClick={clearErrorMessage}>
            Dismiss
          </DismissButton>
        </ErrorMessage>
      )}
      <form onSubmit={checkCaptcha}>
        <Stack space={3}>
          <Stack space={1}>
            <Checkbox
              id='general'
              label='I have a general enquiry'
              {...register('enquiryType')}
              type='radio'
              value='general'
              defaultChecked={true}
            />
            <Checkbox
              id='tour'
              label='I want to book a tour'
              {...register('enquiryType')}
              type='radio'
              value='tour'
            />
          </Stack>
          {enquiryType === 'tour' && (
            <FormGroup>
              <Select
                id='location'
                {...register('location')}
                label='Select location'
              >
                {locations.map((location) => (
                  <option>{location.name}</option>
                ))}
              </Select>
            </FormGroup>
          )}
          <FormGroup>
            <Input
              id='firstName'
              {...register('firstName')}
              label='First Name'
              type='text'
              errors={errors}
            />
            <Input
              id='lastName'
              {...register('lastName')}
              label='Last Name'
              type='text'
              errors={errors}
            />
            <Input
              id='phone'
              {...register('phone')}
              label='Phone'
              type='text'
              errors={errors}
            />
            <Input
              id='email'
              {...register('email')}
              label='Email'
              type='email'
              errors={errors}
            />
          </FormGroup>
          <Input
            id='message'
            {...register('message')}
            label={messageLabel}
            as='textarea'
            errors={errors}
          />
          <Button type='submit' disabled={isSubmitting}>
            {buttonText}
          </Button>
          {process.env.NEXT_PUBLIC_HCAPTCHA_SITEKEY && (
            <HCaptcha
              sitekey={process.env.NEXT_PUBLIC_HCAPTCHA_SITEKEY}
              size='invisible'
              onVerify={setToken}
              onError={onError}
              onExpire={onExpire}
              ref={captchaRef}
            />
          )}
        </Stack>
      </form>
    </Wrapper>
  );
};

export default ContactForm;
